import type { Case } from '../types';
import { normalizeQuoteLineColumns, type QuoteLineColumnConfig } from '../utils/quoteLineColumns';
import {
  calculateQuoteTotals,
  normalizeQuoteTotalSettings,
  type QuoteTotalSettings,
  type QuoteTotalsSummary
} from '../utils/quoteTotals';

export type AnalysisStageId =
  | 'PROBING'
  | 'MODEL_BUILDING'
  | 'MODEL_ANALYSIS'
  | 'MODEL_ENRICHMENT'
  | 'PARTS_TOOLS'
  | 'COMPLETE';
export type StageStatus = 'pending' | 'active' | 'complete' | 'error';

export interface StageDefinition {
  id: AnalysisStageId;
  label: string;
  completedLabel?: string;
  defaultMessage: string;
  startProgress: number;
  completeProgress: number;
}

export const STAGE_SEQUENCE: StageDefinition[] = [
  {
    id: 'PROBING',
    label: 'Refining Problem',
    completedLabel: 'Problem Refined',
    defaultMessage: 'Analysing customer problem and symptoms',
    startProgress: 5,
    completeProgress: 20
  },
  {
    id: 'MODEL_BUILDING',
    label: 'Refining Analysis',
    completedLabel: 'Analysis Refined',
    defaultMessage: 'Refining technical details for this case',
    startProgress: 22,
    completeProgress: 38
  },
  {
    id: 'MODEL_ANALYSIS',
    label: 'Identifying Possible Causes',
    completedLabel: 'Possible Causes Identified',
    defaultMessage: 'Assessing possible causes for the described problem and symptoms',
    startProgress: 40,
    completeProgress: 58
  },
  {
    id: 'MODEL_ENRICHMENT',
    label: 'Creating Troubleshooting Plans',
    completedLabel: 'Troubleshooting Plans Created',
    defaultMessage: 'Building troubleshooting Action Plans for each possible cause',
    startProgress: 60,
    completeProgress: 78
  },
  {
    id: 'PARTS_TOOLS',
    label: 'Identifying Parts and Tools',
    completedLabel: 'Parts and Tools Identified',
    defaultMessage: 'Evaluating tools and parts that are implicated within the Action Plans for each possible cause',
    startProgress: 80,
    completeProgress: 95
  },
  {
    id: 'COMPLETE',
    label: 'Analysis Complete',
    defaultMessage: 'Analysis complete',
    startProgress: 100,
    completeProgress: 100
  }
];

export const STAGE_MAP: Record<AnalysisStageId, StageDefinition> = STAGE_SEQUENCE.reduce(
  (acc, stage) => {
    acc[stage.id] = stage;
    return acc;
  },
  {} as Record<AnalysisStageId, StageDefinition>
);
export const STAGE_INDEX_MAP: Record<AnalysisStageId, number> = STAGE_SEQUENCE.reduce(
  (acc, stage, index) => {
    acc[stage.id] = index;
    return acc;
  },
  {} as Record<AnalysisStageId, number>
);

export type StageState = {
  status: StageStatus;
  message: string;
  durationMs?: number;
};

export type StageStateMap = Record<AnalysisStageId, StageState>;

export const formatDuration = (durationMs?: number): string => {
  if (!durationMs || durationMs <= 0) return '';
  const totalSeconds = Math.round(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  return `${seconds}s`;
};

export const formatDateTime = (value?: string | Date | null): string | null => {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export interface SourceSnippet {
  recordId: string;
  domain?: string;
  title?: string;
  summary?: string;
  highlights?: string[];
  procedures?: string[];
  tools?: string[];
  parts?: string[];
  references?: string[];
  warnings?: string[];
  indicators?: string[];
  sourcePath?: string;
}

export interface ReferenceImageHit {
  id: string;
  label?: string;
  hint?: string;
  note?: string;
  viewType?: string;
  primaryPartNumber?: string;
  partNumbers?: string[];
  components?: string[];
  subassemblies?: string[];
  taskTags?: string[];
  stateTags?: string[];
  systemPaths?: string[];
  viewTags?: string[];
  linkScore?: number | null;
  linkRelation?: string;
  linkedNodeIds?: string[];
  linkedSnippetIds?: string[];
  linkedDomains?: string[];
  matched?: Record<string, unknown> | null;
  bestFor?: string[];
  fileUrl?: string;
}

export interface ReferenceImageAnchor {
  text: string;
  imageIds?: string[];
}

export type ReferenceImageBlockSection =
  | 'SUMMARY'
  | 'VERIFICATION'
  | 'REPAIR'
  | 'TOOLS'
  | 'PARTS'
  | 'NOTES';

export interface ReferenceImageBlock {
  section?: ReferenceImageBlockSection;
  itemId?: string;
  text: string;
  imageIds?: string[];
  sourceNodeIds?: string[];
  anchors?: ReferenceImageAnchor[];
}

export interface StepNode {
  number: string;
  description: string;
  sourceNodeIds?: string[];
  requiresCorrectiveActionIfFailed?: boolean;
  triggeredByVerificationStepNumbers?: string[];
  sourceVerificationStepNumbers?: string[];
  repairActionType?: 'CORRECTIVE_ACTION' | string;
  correctiveMechanism?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
  capture?: Record<string, unknown>;
  subSteps?: StepNode[];
}

export interface PlanAssistantMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface PlanStepOption {
  key: string;
  section: 'VERIFICATION' | 'REPAIR';
  number: string;
  description: string;
}

export type CauseActionType = 'PROCEDURE_EDITED' | 'INACTIVATED' | 'REACTIVATED' | 'RESTORED' | 'REGENERATED';

export interface CauseActionLogEntry {
  timestamp: string;
  type: CauseActionType;
  notes?: string;
}

export interface PossibleCause {
  id: string;
  description: string;
  shortTitle?: string;
  probability: 'HIGH' | 'MEDIUM' | 'LOW';
  testProcedure: string;
  testingSteps?: string[];
  repairSteps?: string[];
  planNotes?: string[];
  summary?: string;
  structuredTestingSteps?: StepNode[];
  structuredRepairSteps?: StepNode[];
  requiredTools: string[];
  requiredParts: string[];
  estimatedTime: number; // minutes
  status: 'UNTESTED' | 'PASSED' | 'FAILED' | 'N/A' | 'INACTIVE';
  eliminates: string[];
  details?: string[];
  sourceIds?: string[];
  sourceNodeIds?: string[];
  causeEvidenceNodeIds?: string[];
  enrichmentEvidenceNodeIds?: string[];
  enrichmentEvidenceSelection?: Record<string, unknown>;
  failureModes?: string[];
  documentReference?: {
    sectionId: string;
    sectionTitle: string;
    pageNumber?: number;
  };
  techSuggested?: boolean;
  secondPass?: boolean;
  causeOrigin?: 'TECH_SUGGESTED' | 'SECOND_PASS';
  unverifiedPlaceholder?: boolean;
  manualSuggestion?: string;
  knowledgeGapReason?: string;
  planUnavailableReason?: string;
  evidenceSource?: string;
  supplementalEvidenceKey?: string;
  focusedRetrievalQuery?: string;
  domain?: string;
  failureMode?: string;
  component?: string;
  sourceSnippets?: SourceSnippet[];
  actionPlanCitations?: Record<string, unknown>;
  referenceImages?: ReferenceImageHit[];
  referenceImageBlocks?: ReferenceImageBlock[];
  diagnosticReasoning?: {
    componentRole?: string;
    symptomMechanism?: string;
    whyRelevant?: string;
    failureChain?: string;
    testRationale?: string;
  };
  procedureEditedByTech?: boolean;
  procedureEditNotes?: string;
  procedureEditedAt?: string;
  procedureRegeneratedAt?: string;
  inactiveReason?: string;
  inactiveAt?: string;
  inactiveHistory?: Array<{ reason: string; timestamp: string }>;
  actionLog?: CauseActionLogEntry[];
}

export interface TestStep {
  id: string;
  causeId: string;
  description: string;
  completed: boolean;
  action: 'TEST' | 'EDIT_PROCEDURE' | 'INACTIVATE' | 'REACTIVATE' | 'RESTORE' | 'REGENERATE';
  result?: 'PASS' | 'FAIL' | 'N/A';
  notes?: string;
  timestamp?: Date;
}

export interface DocumentationFeedback {
  feedbackType: string;
  severity: string;
  title: string;
  description: string;
  impact?: string;
}

export interface TSFADDraftPayload {
  version: number;
  caseId: string;
  analysisId: string | null;
  savedAt: string;
  possibleCauses: PossibleCause[];
  testSteps: TestStep[];
  implementedKitSelections?: ImplementedKitSelection[];
  selectedCauseId: string | null;
  analysisStatus: 'NONE' | 'COMPLETED' | 'ACCEPTED';
}

export interface ImplementedKitSelection {
  kitId: string;
  kitNumber?: string | null;
  sku?: string | null;
  name?: string | null;
  matchedPartNumbers: string[];
  implementedAt: string;
}

export interface QuoteLineItemDraft {
  id: string;
  lineType: 'part' | 'labor';
  description: string;
  quantity: number;
  unitValue: number;
  isFree: boolean;
  source: string;
  catalogId?: string | null;
  sku?: string | null;
  partNumber?: string | null;
  originalUnitValue?: number | null;
  slaCoverageStatus?: 'COVERED' | 'DISCOUNTED' | null;
  slaLabel?: string | null;
  slaDiscountPercent?: number | null;
  slaCoverageReason?: string | null;
  slaMatchedListNames?: string[];
}

export interface QuoteCatalogPartItem {
  id: string;
  sku: string | null;
  partNumber: string | null;
  description: string;
  unitValue: number;
  lineType: string;
}

export interface SavedTSFADQuote {
  quoteId: string;
  analysisId: string;
  status?: string;
  inactiveReason?: string;
  inactiveFromStatus?: string;
  revisionOfQuoteId?: string | null;
  templateId?: string | null;
  templateName?: string | null;
  title: string;
  description?: string;
  lineItems: QuoteLineItemDraft[];
  totals: QuoteTotalsSummary & {
    totalSettings?: QuoteTotalSettings;
    approvalSettings?: {
      responseActions?: string[];
      approvalPlacement?: string;
      approvalFooterSlots?: string[];
      requireSignature?: boolean;
      signatureLabel?: string;
      requireTechnicianSignature?: boolean;
      technicianSignatureLabel?: string;
    };
  };
  totalSettings?: QuoteTotalSettings | null;
  responseActions?: string[];
  approvalPlacement?: string;
  approvalFooterSlots?: string[];
  requireSignature?: boolean;
  signatureLabel?: string;
  requireTechnicianSignature?: boolean;
  technicianSignatureLabel?: string;
  notes?: string;
  customerApprovalName?: string;
  customerApprovalSignature?: string;
  unmatchedParts?: string[];
  savedAt?: string;
  occurredAt?: string;
  actorName?: string | null;
  customerDecision?: 'ACCEPT' | 'DENY' | 'ADJUST' | null;
  decisionAt?: string | null;
  decisionBy?: string | null;
  decisionNote?: string;
}

export interface QuoteTemplateField {
  id: string;
  label: string;
  sourceGroup: string;
  sourceKey: string;
  included: boolean;
}

export interface QuoteTemplateInfoBlock {
  id: string;
  label: string;
  sourceGroup: string;
  included: boolean;
  fields: QuoteTemplateField[];
}

export interface QuoteTemplateLayout {
  id: string;
  name: string;
  headerRows: 1 | 2;
  headerAssignments: Record<string, string | null>;
  infoBlocks: QuoteTemplateInfoBlock[];
  lineColumns: QuoteLineColumnConfig[];
  totalSettings: QuoteTotalSettings;
  descriptionEnabled: boolean;
  descriptionDefault: string;
  responseActions: string[];
  approvalPlacement: 'after_lines' | 'after_totals' | 'footer_c1' | 'footer_c2' | 'footer_c3';
  approvalFooterSlots: ('footer_c1' | 'footer_c2' | 'footer_c3')[];
  requireSignature: boolean;
  signatureLabel: string;
  requireTechnicianSignature: boolean;
  technicianSignatureLabel: string;
  allowTsfadAutoFill: boolean;
}

export const createFeedbackDefaults = (): DocumentationFeedback => ({
  feedbackType: 'MISSING_CAUSE',
  severity: 'MEDIUM',
  title: '',
  description: '',
  impact: ''
});

export const createInitialStageState = (): StageStateMap => ({
  PROBING: { status: 'pending', message: '' },
  MODEL_BUILDING: { status: 'pending', message: '' },
  MODEL_ANALYSIS: { status: 'pending', message: '' },
  MODEL_ENRICHMENT: { status: 'pending', message: '' },
  PARTS_TOOLS: { status: 'pending', message: '' },
  COMPLETE: { status: 'pending', message: '' }
});

export const TSFAD_DRAFT_VERSION = 1;

export const getTSFADDraftStorageKey = (caseId: string): string =>
  `tsfad.analysis.draft.${caseId}`;

export const loadTSFADDraft = (caseId: string): TSFADDraftPayload | null => {
  if (typeof window === 'undefined' || !caseId) return null;
  try {
    const raw = window.localStorage.getItem(getTSFADDraftStorageKey(caseId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TSFADDraftPayload;
    if (!parsed || parsed.version !== TSFAD_DRAFT_VERSION) return null;
    if (parsed.caseId !== caseId) return null;
    if (!Array.isArray(parsed.possibleCauses) || !Array.isArray(parsed.testSteps)) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveTSFADDraft = (caseId: string, payload: TSFADDraftPayload): void => {
  if (typeof window === 'undefined' || !caseId) return;
  window.localStorage.setItem(getTSFADDraftStorageKey(caseId), JSON.stringify(payload));
};

export const clearTSFADDraft = (caseId: string): void => {
  if (typeof window === 'undefined' || !caseId) return;
  window.localStorage.removeItem(getTSFADDraftStorageKey(caseId));
};

export const normaliseLookupToken = (value: string): string =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[^A-Z0-9-]/g, '');

export const extractQuoteLookupTokens = (value: string): string[] => {
  const raw = String(value || '').trim();
  if (!raw) return [];

  const tokens = new Set<string>();
  const addToken = (candidate: string) => {
    const normalised = normaliseLookupToken(candidate);
    if (!normalised) return;
    if (!/\d/.test(normalised)) return;
    if (normalised.length < 4) return;
    tokens.add(normalised);
  };

  const parenthesized = raw.match(/\(([A-Za-z0-9-]{4,})\)/g) || [];
  parenthesized.forEach(match => addToken(match.replace(/[()]/g, '')));

  const longNumeric = raw.match(/\b\d{5,}\b/g) || [];
  longNumeric.forEach(addToken);

  return Array.from(tokens);
};

export const stripFailureRateSuffix = (value: string): string =>
  String(value || '')
    .replace(/\(\s*[-+]?\d+(?:\.\d+)?%\s*\)\s*$/g, '')
    .trim();

export const normalizeQuoteFieldText = (value: string): string => {
  const raw = String(value || '');
  if (!raw) return '';
  return raw
    .replace(/\uFFFD/g, '')
    .replace(/Ã˜|Ã¸/g, '')
    .replace(/[ØøΦφ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

export const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const cleanQuotePartDescription = (rawText: string, partRef: string | null): string => {
  let text = stripFailureRateSuffix(String(rawText || ''));
  const ref = String(partRef || '').trim();
  if (ref) {
    const escaped = escapeRegExp(ref);
    text = text.replace(new RegExp(`\\(${escaped}\\)\\s*$`, 'i'), '').trim();
  }
  return normalizeQuoteFieldText(text);
};

export const normalizeDeviceModel = (value: string): string =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export const isLaborCatalogEntry = (item: any): boolean =>
  String(item?.lineType || '')
    .trim()
    .toLowerCase()
    .includes('labor');

export const buildQuoteDevicePartCatalog = (
  pricingItemsRaw: any[],
  failureRateItemsRaw: any[],
  deviceModelRaw: string
): QuoteCatalogPartItem[] => {
  const pricingItems = Array.isArray(pricingItemsRaw)
    ? pricingItemsRaw.filter(item => item?.isActive !== false)
    : [];
  const partPricingItems = pricingItems.filter(item => !isLaborCatalogEntry(item));

  const byPartNumber = new Map<string, any>();
  const bySku = new Map<string, any>();
  partPricingItems.forEach(item => {
    const partKey = normaliseLookupToken(String(item?.partNumber || ''));
    const skuKey = normaliseLookupToken(String(item?.sku || ''));
    if (partKey && !byPartNumber.has(partKey)) byPartNumber.set(partKey, item);
    if (skuKey && !bySku.has(skuKey)) bySku.set(skuKey, item);
  });

  const failureRateItems = Array.isArray(failureRateItemsRaw) ? failureRateItemsRaw : [];
  const modelKey = normalizeDeviceModel(deviceModelRaw);
  const scopedFailureRows = modelKey
    ? failureRateItems.filter(item => normalizeDeviceModel(String(item?.deviceModel || '')) === modelKey)
    : failureRateItems;

  const scopedKeys = new Set<string>();
  scopedFailureRows.forEach(item => {
    const partKey = normaliseLookupToken(String(item?.partNumber || ''));
    const skuKey = normaliseLookupToken(String(item?.sku || ''));
    if (partKey) scopedKeys.add(partKey);
    if (skuKey) scopedKeys.add(skuKey);
  });

  let scopedCatalog = partPricingItems;
  if (scopedKeys.size > 0) {
    const narrowed = partPricingItems.filter(item => {
      const partKey = normaliseLookupToken(String(item?.partNumber || ''));
      const skuKey = normaliseLookupToken(String(item?.sku || ''));
      return (partKey && scopedKeys.has(partKey)) || (skuKey && scopedKeys.has(skuKey));
    });
    if (narrowed.length > 0) scopedCatalog = narrowed;
  }

  const deduped = new Map<string, QuoteCatalogPartItem>();
  scopedCatalog.forEach(item => {
    const partNumber = String(item?.partNumber || '').trim() || null;
    const sku = String(item?.sku || '').trim() || null;
    const key = normaliseLookupToken(partNumber || sku || '');
    if (!key) return;
    if (deduped.has(key)) return;
    deduped.set(key, {
      id: String(item?.id || ''),
      sku,
      partNumber,
      description: normalizeQuoteFieldText(
        String(item?.description || item?.name || partNumber || sku || '').trim()
      ),
      unitValue: Number(item?.unitValue || 0),
      lineType: String(item?.lineType || 'part')
    });
  });

  return Array.from(deduped.values()).sort((a, b) => {
    const aKey = String(a.partNumber || a.sku || '').trim();
    const bKey = String(b.partNumber || b.sku || '').trim();
    return aKey.localeCompare(bKey);
  });
};

export const computeQuoteTotals = (
  lineItems: QuoteLineItemDraft[],
  totalSettings?: unknown
): QuoteTotalsSummary => calculateQuoteTotals(lineItems, totalSettings);

export const createQuoteLineId = (prefix: string, index: number) =>
  `${prefix}-${Date.now()}-${index + 1}`;

export const normalizeQuoteTemplateLayout = (raw: any): QuoteTemplateLayout => {
  const normalizeQuoteHeaderSlotId = (rawSlotId: any): string | null => {
    if (typeof rawSlotId !== 'string' && typeof rawSlotId !== 'number') return null;
    const normalized = String(rawSlotId)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .trim();
    const slotMatch = normalized.match(/^h([12])c([123])$/);
    if (!slotMatch) return null;
    return `h${slotMatch[1]}c${slotMatch[2]}`;
  };
  const rawHeaderAssignments =
    raw?.headerAssignments && typeof raw.headerAssignments === 'object'
      ? raw.headerAssignments
      : {};
  const headerAssignments: Record<string, string | null> = {};
  Object.keys(rawHeaderAssignments).forEach(slotKey => {
    const normalizedSlotId = normalizeQuoteHeaderSlotId(slotKey);
    if (!normalizedSlotId) return;
    const value = (rawHeaderAssignments as Record<string, any>)[slotKey];
    if (value === null) {
      headerAssignments[normalizedSlotId] = null;
      return;
    }
    const normalisedValue = typeof value === 'string' ? value.trim() : String(value || '').trim();
    headerAssignments[normalizedSlotId] =
      normalisedValue.length > 0 ? normalisedValue : null;
  });
  const infoBlocks = Array.isArray(raw?.infoBlocks)
    ? raw.infoBlocks.map((block: any, index: number) => ({
        id: String(block?.id || `qblock-${index + 1}`),
        label: String(block?.label || `Block ${index + 1}`),
        sourceGroup: String(block?.sourceGroup || 'quote'),
        included: block?.included !== false,
        fields: Array.isArray(block?.fields)
          ? block.fields.map((field: any, fieldIndex: number) => ({
              id: String(field?.id || `${block?.id || `qblock-${index + 1}`}-field-${fieldIndex + 1}`),
              label: String(field?.label || `Field ${fieldIndex + 1}`),
              sourceGroup: String(field?.sourceGroup || block?.sourceGroup || 'quote'),
              sourceKey: String(field?.sourceKey || ''),
              included: field?.included !== false
            }))
          : []
      }))
    : [];

  const rawApprovalPlacement = String(raw?.approvalPlacement || raw?.totals?.approvalSettings?.approvalPlacement || '').trim();
  const rawApprovalFooterSlots = raw?.approvalFooterSlots || raw?.totals?.approvalSettings?.approvalFooterSlots;
  const approvalFooterSlots: ('footer_c1' | 'footer_c2' | 'footer_c3')[] = [];
  const addApprovalFooterSlot = (value: any) => {
    const rawSlot = String(value || '').trim();
    if (
      (rawSlot === 'footer_c1' || rawSlot === 'footer_c2' || rawSlot === 'footer_c3') &&
      !approvalFooterSlots.includes(rawSlot)
    ) {
      approvalFooterSlots.push(rawSlot);
    }
  };
  if (Array.isArray(rawApprovalFooterSlots)) rawApprovalFooterSlots.forEach(addApprovalFooterSlot);
  if (approvalFooterSlots.length === 0) addApprovalFooterSlot(rawApprovalPlacement);
  approvalFooterSlots.sort(
    (a, b) => ['footer_c1', 'footer_c2', 'footer_c3'].indexOf(a) - ['footer_c1', 'footer_c2', 'footer_c3'].indexOf(b)
  );
  return {
    id: String(raw?.id || 'quote-template'),
    name: String(raw?.name || 'Quote'),
    headerRows: raw?.headerRows === 1 ? 1 : 2,
    headerAssignments,
    infoBlocks,
    lineColumns: normalizeQuoteLineColumns(raw?.lineColumns),
    totalSettings: normalizeQuoteTotalSettings(raw?.totalSettings || raw?.totals?.totalSettings),
    descriptionEnabled: raw?.descriptionEnabled !== false,
    descriptionDefault: String(raw?.descriptionDefault || ''),
    responseActions: Array.isArray(raw?.responseActions)
      ? raw.responseActions.map((action: any) => String(action))
      : [],
    approvalPlacement:
      approvalFooterSlots.length > 0
        ? approvalFooterSlots[0]
        : rawApprovalPlacement === 'footer_c1' ||
          rawApprovalPlacement === 'footer_c2' ||
          rawApprovalPlacement === 'footer_c3'
          ? rawApprovalPlacement
        : rawApprovalPlacement === 'footer'
          ? 'footer_c1'
        : 'footer_c3',
    approvalFooterSlots,
    requireSignature: raw?.requireSignature === true,
    signatureLabel: String(raw?.signatureLabel || 'Customer approval signature'),
    requireTechnicianSignature: raw?.requireTechnicianSignature === true,
    technicianSignatureLabel: String(raw?.technicianSignatureLabel || 'Technician signature'),
    allowTsfadAutoFill: raw?.allowTsfadAutoFill !== false
  };
};

export const readPathValue = (source: any, path: string): string => {
  if (!source || !path) return '';
  const parts = String(path).split('.');
  let cursor = source;
  for (const key of parts) {
    if (cursor === null || cursor === undefined || typeof cursor !== 'object') return '';
    cursor = cursor[key];
  }
  if (cursor === null || cursor === undefined) return '';
  return String(cursor);
};

export const getCaseDisplayNumber = (record?: Partial<Case> | null): string => {
  if (!record) return '';
  return record.caseCode || record.caseNumber || record.id || '';
};

